import { useEffect, useMemo, useRef } from 'react';
import type { PreviewMessage, WidgetPreviewConfig } from './types';
import { BackIcon } from './widget-icons';
import { MessageList } from './message-list';
import { TypingIndicator } from './typing-indicator';
import { QuickQuestions } from './quick-questions';
import { ContactForm } from './contact-form';
import { MessageComposer } from './message-composer';
import styles from './widget-preview.module.scss';

interface ChatWindowProps {
  config: WidgetPreviewConfig;
  messages: PreviewMessage[];
  showContactForm: boolean;
  showTyping: boolean;
  showFormError: boolean;
  showRecommendations: boolean;
  inputLocked: boolean;
  resetSignal: number;
  onCollapse: () => void;
  onSend: (message: string) => void;
  onContactSubmit: () => void;
  onContactFormClose: () => void;
}

export const ChatWindow = ({
  config,
  messages,
  showContactForm,
  showTyping,
  showFormError,
  showRecommendations,
  inputLocked,
  resetSignal,
  onCollapse,
  onSend,
  onContactSubmit,
  onContactFormClose,
}: ChatWindowProps) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [policyPrefix, policySuffix = ''] =
    config.translations.policyText.split('{{privacyPolicy}}');
  const displayMessages = useMemo(
    () =>
      showContactForm
        ? [
            ...messages,
            {
              id: 'contact-form-message',
              sender: 'assistant' as const,
              text: config.contactForm.messageBeforeForm,
            },
          ]
        : messages,
    [config.contactForm.messageBeforeForm, messages, showContactForm],
  );

  useEffect(() => {
    const area = scrollAreaRef.current;
    if (area) area.scrollTo({ top: area.scrollHeight, behavior: 'smooth' });
  }, [messages.length, showContactForm, showRecommendations, showTyping]);

  return (
    <div className={styles.chatWindow}>
      <button
        type="button"
        className={styles.backButton}
        onClick={onCollapse}
        aria-label={config.translations.backButtonAriaLabel}
      >
        <BackIcon />
      </button>
      <div ref={scrollAreaRef} className={styles.messagesArea}>
        <p className={styles.policyText}>
          {policyPrefix}
          {config.policyUrl ? (
            <a href={config.policyUrl} target="_blank" rel="noreferrer noopener">
              {config.translations.privacyPolicyLabel}
            </a>
          ) : (
            config.translations.privacyPolicyLabel
          )}
          {policySuffix}
        </p>
        <MessageList messages={displayMessages} config={config} />
        {!showContactForm && showRecommendations && config.messages.showAgentAnswerHints && (
          <QuickQuestions
            questions={config.content.quickQuestions}
            scrollLeftAriaLabel={config.translations.recommendationsScrollLeftLabel}
            scrollRightAriaLabel={config.translations.recommendationsScrollRightLabel}
            onSelect={onSend}
          />
        )}
        {showTyping && <TypingIndicator config={config} />}
        {showContactForm && (
          <ContactForm
            brandColor={config.appearance.brandColor}
            lang={config.lang}
            translations={config.translations}
            fields={config.contactForm.fields}
            forceErrors={showFormError}
            resetSignal={resetSignal}
            onSubmit={onContactSubmit}
            onClose={config.contactForm.showCloseButton ? onContactFormClose : undefined}
          />
        )}
      </div>
      {!showContactForm && (
        <MessageComposer
          disabled={inputLocked}
          resetSignal={resetSignal}
          translations={config.translations}
          messageAriaLabel={config.demo.messageInputAriaLabel}
          onSend={onSend}
        />
      )}
    </div>
  );
};
